/**
 * Basecamp Todo List Viewer
 * Full-tab todolist with Continuity gestures (maater-design-ui):
 * single click select (+ preview), double-click open, right-click Preview/Open.
 */

import { useMemo, useEffect, useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertCircle,
  Check,
  Download,
  FileText,
  Info,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Square,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useBasecampTodoLists } from '@/hooks/use-basecamp'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import { ingestBasecampTodolist } from '@/lib/api/ingestion'
import { scheduleOpenIngestionConversation } from '@/lib/open-ingestion-conversation'
import { buildBasecampWebUrl } from '@/lib/basecamp-external-url'
import {
  buildBasecampBreadcrumbTrail,
  dispatchIntegrationTabEnrich,
} from '@/lib/basecamp-breadcrumbs'
import {
  formatRelativeSyncLabel,
  rememberProjectsPanelSelection,
} from '@/lib/basecamp-project-relevance'
import { countTodoListWithSections } from '@/lib/basecamp-todolist-counts'
import { isModClick } from '@/lib/open-intent'
import { OpenDestinationIcons, resolveOpenExternalIcon } from '@/lib/open-destination-icons'
import {
  ExtensionListRow,
  IndexPreviewEmpty,
  SecondaryPreviewResizeHandle,
  useRegisterIndexPreviewPin,
} from '@/components/integrations/_shared'
import { Panel, PanelGroup } from 'react-resizable-panels'
import { EntityContextMenu } from '@/components/features/entity-context-menu'
import { BasecampTodoPreviewPane } from './basecamp-todo-preview-pane'
import { NestedListSection } from './components/nested-list-section'
import { BasecampDueBadge } from './components/basecamp-due-badge'
import { testIds } from '@/lib/test-ids'
import type { BasecampAssignment, BasecampTodo } from '@/types/basecamp'
import { BasecampIcon } from '@/components/icons/basecamp-icon'

interface BasecampTodoListViewerProps {
  listId: string
  projectId: string
  projectName?: string
  organizationId?: string
  onDocumentClick?: (filePath: string) => void
}

type ListTodo = {
  id: string
  title: string
  completed?: boolean
  due_on?: string | null
  url?: string
}

function listTodoAsAssignment(
  todo: ListTodo,
  projectId: string,
  projectName: string,
  listTitle?: string,
  listId?: string,
): BasecampAssignment {
  const todoId = String(todo.id)
  return {
    id: todoId,
    title: todo.title || 'Todo',
    url: todo.url || '',
    due_on: todo.due_on ?? undefined,
    completed: !!todo.completed,
    project: { id: projectId, name: projectName },
    assignee: { id: '', name: '' },
    _project_name: projectName,
    _parent_title: listTitle,
    ...(listId ? { _todolist_id: listId } : {}),
    _app_url: buildBasecampWebUrl(todoId, projectId, 'todo', '5744549'),
  }
}

export function BasecampTodoListViewer({
  listId,
  projectId,
  projectName: propProjectName,
  organizationId,
}: BasecampTodoListViewerProps) {
  const [bulkIngesting, setBulkIngesting] = useState(false)
  const [selectedTodoId, setSelectedTodoId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(true)
  const togglePreview = useCallback(() => setPreviewOpen((v) => !v), [])
  useRegisterIndexPreviewPin({
    open: previewOpen,
    onToggle: togglePreview,
    testId: testIds.basecamp.todoListViewerPreviewToggle,
  })
  const [ctx, setCtx] = useState<
    | {
        kind: 'todo'
        x: number
        y: number
        todoId: string
        todoTitle: string
      }
    | {
        kind: 'section'
        x: number
        y: number
        sectionId: string
        sectionTitle: string
      }
    | null
  >(null)

  const { todoLists, isLoading, error, refetch, cached, cachedAt } =
    useBasecampTodoLists(projectId, { autoRefresh: false })

  const cachedProjectName = projectId
    ? localStorage.getItem(`basecamp-project-name:${projectId}`)
    : null
  const projectName = propProjectName || cachedProjectName || 'Basecamp Project'

  useEffect(() => {
    if (projectId && propProjectName) {
      localStorage.setItem(`basecamp-project-name:${projectId}`, propProjectName)
    }
  }, [projectId, propProjectName])

  const todoList = useMemo(() => {
    return todoLists.find((list) => list.id?.toString() === listId?.toString())
  }, [todoLists, listId])

  const isNested = todoList?.is_nested === true

  const nestedSections = useMemo(() => {
    if (!todoList) return []
    return todoLists.filter(
      (list) =>
        list.parent_id?.toString() === todoList.id?.toString() &&
        list.is_nested === true,
    )
  }, [todoLists, todoList])

  const activeTodos = useMemo(() => {
    if (!todoList) return []
    return todoList.todos.filter((todo) => !todo.completed)
  }, [todoList])

  const completedTodos = useMemo(() => {
    if (!todoList) return []
    return todoList.todos.filter((todo) => todo.completed)
  }, [todoList])

  const allTodosById = useMemo(() => {
    const map = new Map<string, ListTodo>()
    for (const t of activeTodos) map.set(String(t.id), t)
    for (const t of completedTodos) map.set(String(t.id), t)
    for (const section of nestedSections) {
      for (const t of section.todos) map.set(String(t.id), t)
    }
    return map
  }, [activeTodos, completedTodos, nestedSections])

  const selectedTodo = selectedTodoId
    ? allTodosById.get(selectedTodoId) ?? null
    : null

  const selectedAssignment = useMemo(() => {
    if (!selectedTodo) return null
    return listTodoAsAssignment(
      selectedTodo,
      projectId,
      projectName,
      todoList?.title,
      listId,
    )
  }, [selectedTodo, projectId, projectName, todoList?.title, listId])

  const { openCount, completedCount } = useMemo(() => {
    if (!todoList) return { openCount: 0, completedCount: 0 }
    const counts = countTodoListWithSections(todoList, todoLists)
    return { openCount: counts.open, completedCount: counts.completed }
  }, [todoList, todoLists])

  const breadcrumbTrail = useMemo(() => {
    if (!todoList || todoLists.length === 0) return []
    return buildBasecampBreadcrumbTrail({
      projectId,
      projectName,
      listId,
      todoLists,
    })
  }, [projectId, projectName, listId, todoList, todoLists])

  useEffect(() => {
    if (!todoList || breadcrumbTrail.length === 0) return
    dispatchIntegrationTabEnrich({
      tabId: `basecamp-todo-list-${listId}`,
      breadcrumbTrail,
      title: todoList.title,
      listName: todoList.title,
      projectName,
    })
  }, [listId, todoList, breadcrumbTrail, projectName])

  const openTodo = useCallback(
    (
      todoId: string,
      todoTitle: string,
      opts?: { event?: React.MouseEvent; openInNewTab?: boolean },
    ) => {
      rememberProjectsPanelSelection(projectId, organizationId)
      const openInNewTab =
        opts?.openInNewTab === true || isModClick(opts?.event)
      window.dispatchEvent(
        new CustomEvent('integration-view-change', {
          detail: {
            tabId: `basecamp-todo-${todoId}`,
            title: todoTitle,
            type: 'integration-basecamp-todo',
            integrationData: {
              integrationId: 'basecamp',
              projectId,
              todoId,
              projectName,
              listId,
              listName: todoList?.title,
              breadcrumbTrail,
              ...(organizationId ? { organizationId } : {}),
            },
            openInNewTab,
          },
        }),
      )
    },
    [
      projectId,
      organizationId,
      projectName,
      listId,
      todoList?.title,
      breadcrumbTrail,
    ],
  )

  const selectTodo = useCallback((todoId: string) => {
    setSelectedTodoId(todoId)
    setPreviewOpen(true)
  }, [])

  const handleSectionClick = (sectionId: string, sectionTitle: string) => {
    rememberProjectsPanelSelection(projectId, organizationId)
    window.dispatchEvent(
      new CustomEvent('integration-view-change', {
        detail: {
          tabId: `basecamp-todo-list-${sectionId}`,
          title: sectionTitle,
          type: 'integration-basecamp-todo-list',
          integrationData: {
            integrationId: 'basecamp',
            projectId,
            listId: sectionId,
            projectName,
            listName: sectionTitle,
            ...(organizationId ? { organizationId } : {}),
          },
        },
      }),
    )
  }

  const handleBulkIngest = async () => {
    const accountId = '5744549'
    const todolistUrl = buildBasecampWebUrl(
      listId,
      projectId,
      'todolist',
      accountId,
    )
    setBulkIngesting(true)
    try {
      const result = await ingestBasecampTodolist(todolistUrl, {
        skipExisting: true,
        organizationId,
      })
      if (result.conversationId) {
        scheduleOpenIngestionConversation(
          result.conversationId,
          result.threadId,
        )
      }
      toast.success('Bulk ingest started', {
        description: 'Progress will appear in the chat panel.',
      })
    } catch (err) {
      const error = err as Error & {
        conversationId?: string
        threadId?: string
      }
      if (error.conversationId) {
        scheduleOpenIngestionConversation(
          error.conversationId,
          error.threadId,
        )
      }
      toast.error('Bulk ingest failed', {
        description: error.message || 'Unknown error',
      })
    } finally {
      setBulkIngesting(false)
    }
  }

  const handleSync = async () => {
    try {
      await refetch(true)
      toast.success('Synced from Basecamp', {
        description: 'Updated todo lists for this project.',
      })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed')
    }
  }

  const syncMeta = cachedAt
    ? `${cached ? 'Cached' : 'Loaded'} ${formatRelativeSyncLabel(cachedAt)}`
    : cached
      ? 'Cached'
      : null

  if (isLoading && !todoList) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error && !todoList) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <h2 className="mb-2 text-lg font-semibold">Failed to Load Todo List</h2>
          <p className="mb-4 text-sm text-muted-foreground">{error}</p>
          <Button onClick={() => void handleSync()} variant="outline" size="sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Sync from Basecamp
          </Button>
        </div>
      </div>
    )
  }

  if (!todoList) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="max-w-md text-center">
          <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-50" />
          <h2 className="mb-2 text-lg font-semibold">Todo List Not Found</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            This list is not in the local cache for this project yet.
          </p>
          <Button onClick={() => void handleSync()} variant="outline" size="sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Sync project todos
          </Button>
        </div>
      </div>
    )
  }

  const renderTodoRow = (
    todo: ListTodo | BasecampTodo,
    opts?: { completed?: boolean },
  ) => {
    const id = String(todo.id)
    return (
      <li key={`${listId}-${todo.id}${opts?.completed ? '-done' : ''}`}>
        <ExtensionListRow
          selected={selectedTodoId === id && previewOpen}
          onSelect={(e) => {
            if (isModClick(e)) {
              openTodo(id, todo.title, { event: e })
              return
            }
            selectTodo(id)
          }}
          onOpen={(e) => openTodo(id, todo.title, { event: e })}
          onContextMenu={(e) => {
            setSelectedTodoId(id)
            setCtx({
              kind: 'todo',
              x: e.clientX,
              y: e.clientY,
              todoId: id,
              todoTitle: todo.title,
            })
          }}
          className={cn('items-center', opts?.completed && 'opacity-60')}
          testId={testIds.basecamp.todoListViewerRow(id)}
        >
          {opts?.completed ? (
            <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <Square className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
            <div
              className={cn(
                'truncate text-sm font-medium',
                opts?.completed && 'line-through',
              )}
            >
              {todo.title}
            </div>
            {todo.due_on && !opts?.completed ? (
              <div className="mt-1">
                <BasecampDueBadge dueOn={todo.due_on} />
              </div>
            ) : null}
          </div>
        </ExtensionListRow>
      </li>
    )
  }

  const ExternalIcon = resolveOpenExternalIcon(BasecampIcon)

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-background"
      data-testid={testIds.basecamp.todoListViewer}
    >
      <PanelGroup direction="horizontal" className="flex min-h-0 flex-1">
        <Panel minSize={35} className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <div
            className="flex h-11 shrink-0 items-center gap-2 border-b border-border bg-background px-4"
            data-testid={testIds.basecamp.todoListViewerHeader}
          >
            <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {openCount} open
              <span className="mx-1.5 opacity-40">·</span>
              {completedCount} completed
              {nestedSections.length > 0 ? (
                <>
                  <span className="mx-1.5 opacity-40">·</span>
                  {nestedSections.length} sections
                </>
              ) : null}
              {syncMeta ? (
                <>
                  <span className="mx-1.5 opacity-40">·</span>
                  {syncMeta}
                </>
              ) : null}
            </p>
            {isNested ? (
              <Badge variant="secondary" className="text-[10px]">
                Section
              </Badge>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label="List actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
              >
                <DropdownMenuItem
                  className="gap-2 text-xs"
                  disabled={isLoading}
                  onClick={() => void handleSync()}
                >
                  <RefreshCw className="h-3.5 w-3.5 flex-none" />
                  Sync from Basecamp
                </DropdownMenuItem>
                {!isNested ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="gap-2 text-xs"
                      disabled={bulkIngesting || isLoading}
                      onClick={() => void handleBulkIngest()}
                    >
                      <Download className="h-3.5 w-3.5 flex-none" />
                      Ingest all todos
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {nestedSections.length > 0 ? (
              <section>
                <div className="flex items-center justify-between px-6 py-2">
                  <h2 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Sections
                  </h2>
                  <span className="text-[10px] tabular-nums text-muted-foreground">
                    {nestedSections.length}
                  </span>
                </div>
                {nestedSections.map((section) => (
                  <NestedListSection
                    key={section.id}
                    section={section}
                    projectId={projectId}
                    projectName={projectName}
                    selectedTodoId={previewOpen ? selectedTodoId : null}
                    onSelectTodo={(id) => selectTodo(id)}
                    onOpenTodo={(id, title, e) =>
                      openTodo(id, title, { event: e })
                    }
                    onTodoContextMenu={(id, title, e) => {
                      setSelectedTodoId(id)
                      setCtx({
                        kind: 'todo',
                        x: e.clientX,
                        y: e.clientY,
                        todoId: id,
                        todoTitle: title,
                      })
                    }}
                    onSectionClick={handleSectionClick}
                    onSectionContextMenu={(id, title, e) => {
                      setCtx({
                        kind: 'section',
                        x: e.clientX,
                        y: e.clientY,
                        sectionId: id,
                        sectionTitle: title,
                      })
                    }}
                    defaultExpanded={
                      section.todos.some((t) => !t.completed) ||
                      nestedSections.length <= 4
                    }
                  />
                ))}
              </section>
            ) : null}

            {activeTodos.length > 0 ? (
              <section>
                <div className="flex items-center justify-between px-6 py-2">
                  <h2 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {nestedSections.length > 0 ? 'Top-level todos' : 'Open todos'}
                  </h2>
                  <span className="text-[10px] tabular-nums text-muted-foreground">
                    {activeTodos.length}
                  </span>
                </div>
                <ul>{activeTodos.map((todo) => renderTodoRow(todo))}</ul>
              </section>
            ) : null}

            {completedTodos.length > 0 ? (
              <section>
                <div className="flex items-center justify-between px-6 py-2">
                  <h2 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Completed
                  </h2>
                  <span className="text-[10px] tabular-nums text-muted-foreground">
                    {completedTodos.length}
                  </span>
                </div>
                <ul>
                  {completedTodos.map((todo) =>
                    renderTodoRow(todo, { completed: true }),
                  )}
                </ul>
              </section>
            ) : null}

            {nestedSections.length === 0 &&
            activeTodos.length === 0 &&
            completedTodos.length === 0 ? (
              <p className="px-6 py-10 text-center text-xs text-muted-foreground">
                No todos in this list yet.
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2 border-t border-border px-6 py-2 text-[11px] text-muted-foreground">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  aria-label="About list sync"
                >
                  <Info className="h-3 w-3" />
                  Status
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                Lists come from the project cache. Use Sync from the overflow
                menu when counts look stale.
              </TooltipContent>
            </Tooltip>
            <span className="opacity-40">·</span>
            <span>
              Click to select · Double-click open · Right-click for menu
            </span>
          </div>
        </Panel>

        {previewOpen ? (
          <>
            <SecondaryPreviewResizeHandle />
            <Panel
              defaultSize={32}
              minSize={22}
              maxSize={45}
              className="flex min-h-0 flex-col overflow-hidden"
              data-testid={testIds.basecamp.todoListViewerSecondary}
            >
            {selectedAssignment ? (
              <BasecampTodoPreviewPane
                assignment={selectedAssignment}
                onOpen={(event) =>
                  openTodo(selectedAssignment.id, selectedAssignment.title, { event })
                }
                onClose={() => {
                  setPreviewOpen(false)
                  setSelectedTodoId(null)
                }}
                onOpenBasecamp={(url) =>
                  window.open(url, '_blank', 'noopener,noreferrer')
                }
              />
            ) : (
              <IndexPreviewEmpty
                row
                label="Todo preview"
                noun="todo"
                testId={testIds.basecamp.todoListPreviewEmpty}
                onClose={() => {
                  setPreviewOpen(false)
                  setSelectedTodoId(null)
                }}
              />
            )}
          </Panel>
          </>
        ) : null}
      </PanelGroup>

      {ctx ? (
        <EntityContextMenu
          x={ctx.x}
          y={ctx.y}
          onClose={() => setCtx(null)}
          entries={
            ctx.kind === 'section'
              ? [
                  {
                    label: 'Open',
                    icon: OpenDestinationIcons.open,
                    onSelect: () => {
                      handleSectionClick(ctx.sectionId, ctx.sectionTitle)
                      setCtx(null)
                    },
                  },
                ]
              : [
                  {
                    label: 'Preview',
                    icon: OpenDestinationIcons.preview,
                    onSelect: () => {
                      selectTodo(ctx.todoId)
                      setCtx(null)
                    },
                  },
                  {
                    label: 'Open',
                    icon: OpenDestinationIcons.open,
                    onSelect: () => {
                      openTodo(ctx.todoId, ctx.todoTitle)
                      setCtx(null)
                    },
                  },
                  {
                    label: 'Open in new tab',
                    icon: OpenDestinationIcons.openInNewTab,
                    onSelect: () => {
                      openTodo(ctx.todoId, ctx.todoTitle, {
                        openInNewTab: true,
                      })
                      setCtx(null)
                    },
                  },
                  {
                    label: 'Open in Basecamp',
                    icon: ExternalIcon,
                    onSelect: () => {
                      const url = buildBasecampWebUrl(
                        ctx.todoId,
                        projectId,
                        'todo',
                        '5744549',
                      )
                      window.open(url, '_blank', 'noopener,noreferrer')
                      setCtx(null)
                    },
                  },
                ]
          }
        />
      ) : null}
    </div>
  )
}
